/*
    Copyright 2022-2025, Techwise Integrated Security, LLC
*/

import React, { useEffect, useState } from "react";

import {
    Box,
    Grid,
    Stack,
    Button,
    IconButton,
    Tooltip,
    lighten,
    darken,
    useTheme,
    Divider,
    Typography,
    CircularProgress,
} from "@mui/material";

import CancelIcon from "@mui/icons-material/Cancel";

import { useFilePicker } from "use-file-picker";
import { Effect, Nullable } from "../../utils/Interfaces";

import { formatDataSize } from "../../utils/Functions";
import { FileContent } from "use-file-picker/types";
import { useAppDispatch } from "../../app/hooks";
import { AnyAction, Dispatch } from "@reduxjs/toolkit";
import { ModelName, uploadAttachment } from "../../utils/Requests";
import { AddAPhoto } from "@mui/icons-material";
import { useWindowDimensions } from "../../utils/Hooks";

export type ModelComposite = { model: ModelName; keyId: number };

export interface DeferredAttachmentManagerProps {
    model: ModelName;
    idKey: Nullable<number>;
    additionalUploads?: ModelComposite[];
    editing: boolean;
    readOnly: boolean;
    onFilesUploaded: Effect;
}

const DeferredAttachmentManager = (props: DeferredAttachmentManagerProps) => {
    const { model, idKey, editing, readOnly, onFilesUploaded, additionalUploads = [] } = props;

    const dispatch: Dispatch<AnyAction> = useAppDispatch();

    const { width } = useWindowDimensions();

    const [uploading, setUploading] = useState<{ [key: string]: boolean }>({});

    const [dummy, setDummy] = useState<boolean>(false);

    let darkTheme = localStorage.getItem("techwiseTheme") === "dark";

    // @ts-ignore
    const { openFilePicker, filesContent } = useFilePicker({
        accept: "*",
        readAs: "ArrayBuffer",
    });

    function removeArrayElem(array: any[], index: number) {
        array.splice(index, 1);
    }

    function uploadAllFiles(): void {
        // Use this as a mutex so that only 1 request get request is made once all files have been uploaded.
        let uploads: number = 0;

        let allUploads: ModelComposite[] = [{ model: model, keyId: idKey as number }];
        additionalUploads.map((upload: ModelComposite) => allUploads.push(upload));

        allUploads.map((modelComposite: ModelComposite) => {
            filesContent.map((_file: FileContent<ArrayBuffer>): null => {
                uploads++;
                uploadAttachment(modelComposite.model, modelComposite.keyId, _file.name, _file.content, dispatch, () => {
                    uploads--;
                    if (uploads !== 0) {
                        return;
                    }
                    setUploading({});
                    while (filesContent.length > 0) {
                        removeArrayElem(filesContent, 0);
                    }
                    onFilesUploaded();
                });

                return null;
            });

            return null;
        });

        // No files provided, trigger callback.
        if (uploads === 0) {
            onFilesUploaded();
        }
    }

    useEffect(() => {
        if (idKey === null) {
            return;
        }

        uploadAllFiles();
    }, [idKey]);

    useEffect(
        () => {
            filesContent.map((fileContent) => (uploading[fileContent.name] = false));
        },
        // eslint-disable-next-line
        [filesContent]
    );

    const theme = useTheme();

    return (
        <Stack direction="column">
            {filesContent.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Divider>
                        <Typography sx={{ fontSize: 16 }} color="text.primary">
                            Files to Upload
                        </Typography>
                    </Divider>
                </Box>
            )}

            {filesContent.map((file: FileContent<ArrayBuffer>, index: number) => {
                let background = lighten(theme.palette.background.paper, darkTheme ? 0.1 : 0.07);

                if (index % 2 === (darkTheme ? 1 : 0)) {
                    background = darken(background, darkTheme ? 0.25 : 0.05);
                }

                return (
                    <Grid
                        container
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        style={{ background: background, paddingTop: "5px", paddingLeft: "10px", paddingBottom: "0px" }}
                        key={index}
                    >
                        <Box>
                            <Stack sx={{ display: "flex", flexGrow: 1 }}>
                                <Box>{file.name}</Box>
                                <Typography variant="caption" sx={{ opacity: 0.65, mb: 0.5 }}>
                                    {formatDataSize(file.content.byteLength)}
                                </Typography>
                            </Stack>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
                            {uploading[file.name] && <CircularProgress size={20} />}
                            {!uploading[file.name] && (
                                <Tooltip title="Cancel Upload">
                                    <IconButton
                                        onClick={() => {
                                            removeArrayElem(filesContent, index);
                                            setDummy(!dummy);
                                        }}
                                        size="large"
                                    >
                                        <CancelIcon />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </Box>
                    </Grid>
                );
            })}

            {editing && filesContent.length > 0 && (
                <Grid container direction="row" justifyContent="space-between" alignItems="center" style={{ paddingTop: "5px" }}>
                    <Grid>
                        <Button onClick={() => openFilePicker()}>Reselect Files</Button>
                    </Grid>
                </Grid>
            )}

            {editing && filesContent.length === 0 && (
                <Box
                    sx={{
                        border: darkTheme ? "3px dashed #FFFFFF" : "3px dashed #000000",
                        borderRadius: 6,
                        opacity: readOnly ? 0.15 : darkTheme ? 0.35 : 0.5,
                        transition: "opacity 0.15s ease-in-out 0s",
                        ":hover": {
                            opacity: readOnly ? 0.15 : darkTheme ? 0.5 : 0.75,
                        },
                        mt: 3,
                        height: 125,
                        background: darkTheme ? "rgba(255, 255, 255, 0.10)" : "rgba(0, 0, 0, 0.05)",
                        cursor: readOnly ? "not-allowed" : "pointer",
                    }}
                    onClick={() => {
                        if (readOnly) {
                            return;
                        }
                        openFilePicker();
                    }}
                >
                    <Stack direction="row" justifyContent={width > 1000 ? "space-between" : "center"}>
                        <AddAPhoto fontSize="large" sx={{ transform: "scale(2.0)", mt: 5, ml: width > 1000 ? 5 : 0 }} />
                        {width > 1000 && (
                            <Box sx={{ mt: 5, mr: 5 }}>
                                <Typography textAlign="left" variant="h5">
                                    Upload Photo or Attachment
                                </Typography>
                                <Typography textAlign="left" variant="subtitle2">
                                    (Click or Tap to Upload)
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Box>
            )}
        </Stack>
    );
};

export default DeferredAttachmentManager;
